import { z } from 'zod'

import type { StepChrome } from '@/components/layout/page-chrome'
import type { VehicleAvailability } from '@/features/calendar/queries'
import {
  addDays,
  dateOf,
  dayKey,
  dayOfMonth,
  isCalendarDate,
  timeOfDay,
  today as todayIn,
  weekdayShort,
  type CalendarDate,
} from '@/lib/time'
import type { BookingsSettings, OpeningHours } from '@/features/settings/queries'
import type { Vehicle } from '@/features/vehicles/queries'

import type { BookingPrefill, BookingWindow } from './booking-window'
import type { PickedCustomer } from './customer-picker'
import {
  asksWhen,
  DEFAULT_PRESET,
  defaultLength,
  DRIVE_PRESETS,
  PRESET_KINDS,
  writesBooking,
  type DrivePreset,
} from './drive-presets'
import type { BookingKind, FreeWindow } from './queries'

/**
 * The guided drive as a state machine, with none of its screen.
 *
 * Three questions — which car, when, who is driving — each of which has exactly one answer, and
 * each of whose answers invalidates the ones after it: a different car is free at different
 * times, so choosing one throws away a time that was chosen against the old one. Those rules are
 * the whole of this file, and they are here rather than inside the component because they are
 * what is worth being sure of.
 *
 * **The middle question is asked by one tile out of six.** The first screen opens on `Now`
 * (`drive-presets.ts`), which starts the drive at the signature and works its own return out, so
 * a standard walk-in answers two questions and never sees the free-window picker. Only `Book for
 * later` keeps all three and writes a `bookings` row; the rest write an `activities` row with a
 * NULL `booking_id`, which is what the partial indexes were built for (ADR 0005).
 *
 * Nothing here reads the browser's clock or its zone. Every instant goes out through
 * `lib/time`, which is the one place wall time happens.
 */

export type FlowStep = 1 | 2 | 3

const FLOW_STEPS = [1, 2, 3] as const satisfies readonly FlowStep[]

/**
 * The questions this preset actually asks.
 *
 * Only `Book for later` asks when. Every other tile starts the drive at the signature and works
 * its own return out (`drive-presets.ts`), so the free-window picker — the screen the client says
 * should not appear for 90–95% of drives — is not a step at all: the flow is which car, then who
 * is driving.
 */
export function flowSteps(preset: DrivePreset): readonly FlowStep[] {
  return asksWhen(preset) ? FLOW_STEPS : ([1, 3] as const)
}

/**
 * The step the flow is on, as the one value the chrome publishes: the prototype's one continuous
 * bar, with the counter written out (`Step 2 of 3`) where the hand-over's canvas writes it short.
 * This scale is the guided flow's own and not the hand-over's eight, and it counts the questions
 * this preset asks — a walk-in that never sees the time picker is on step 2 of 2, not 3.
 */
export function flowStep(step: FlowStep, preset: DrivePreset): StepChrome {
  const steps = flowSteps(preset)
  return {
    current: steps.indexOf(step) + 1,
    total: steps.length,
    meter: 'bar',
    short: false,
    note: null,
  }
}

const LAST_STEP: FlowStep = 3

/** The question after this one, for the footer's button. The last one writes instead. */
export function nextStep(step: FlowStep, preset: DrivePreset): FlowStep {
  const steps = flowSteps(preset)
  return steps[steps.indexOf(step) + 1] ?? LAST_STEP
}

/**
 * Route search params for the flow.
 *
 * `step` is in the URL rather than in component state so that the phone's back arrow — which is
 * the browser's history, not a control this screen draws — steps back through the questions
 * instead of leaving the flow. The rest is what the caller already knew: the vehicle from a
 * vehicle's own page, the customer from theirs, the day from the calendar, the kind from Today's
 * two buttons. Every field falls back on its own, so a mangled link opens the flow at question
 * one rather than failing.
 */
export const bookingFlowSearchSchema = z
  .object({
    step: z
      .union([z.literal(1), z.literal(2), z.literal(3)])
      .optional()
      .catch(undefined),
    kind: z.enum(['test_drive', 'loan']).optional().catch(undefined),
    /**
     * The tile the first screen opens on. Absent is `Now`, which is the point of the tile.
     *
     * In the URL beside the step so that a link into the flow can ask for a loan or for the
     * picker — the calendar's tapped gap wants `later` — and so the phone's back arrow steps
     * back through the questions this tile asks rather than the three another one would.
     */
    preset: z.enum(DRIVE_PRESETS).optional().catch(undefined),
    vehicle: z.uuid().optional().catch(undefined),
    date: z.string().refine(isCalendarDate).optional().catch(undefined),
    /** Only the id: step 3 reads the name and the mobile off the record itself. */
    customer: z.uuid().optional().catch(undefined),
    /**
     * The last screen before the signature, on every tile but `Book for later`.
     *
     * A flag rather than a fourth step, because it is not a question: the three answers are in
     * and this is the confirmation of them — the customer, the car, the kind of drive and when it
     * is due back. In the URL for the same reason the step is, so the back arrow returns to the
     * driver question rather than leaving the flow.
     */
    review: z.literal(true).optional().catch(undefined),
    /**
     * The booking that was made, once one has been: the confirmation's whole state.
     *
     * Only the id, and for the same reason the customer is only an id — the reference, the name
     * and the window are read back off the record rather than carried in a link that anybody can
     * edit. While it is set the questions are over and the confirmation is what the route draws.
     */
    booked: z.uuid().optional().catch(undefined),
  })
  .catch({})

export type BookingFlowSearch = z.infer<typeof bookingFlowSearchSchema>

/**
 * What the caller knew, as the URL carries it. A tapped gap becomes the day it was on.
 *
 * The tile follows from what they knew. Somebody who tapped a gap on the calendar has already
 * said when, so the flow opens on `Book for later` and keeps the picker; somebody who pressed
 * `Loan car` opens on the loan tile; everybody else gets `Now`, which is the default and needs
 * no entry here.
 */
export function flowSearchFor(prefill: BookingPrefill, timeZone: string): BookingFlowSearch {
  return {
    kind: prefill.kind,
    preset: presetFor(prefill),
    vehicle: prefill.vehicleId,
    // A gap only ever *caps* a drive, and the grid below offers whole windows rather than
    // fragments of one, so what survives the trip is the day the gap was on.
    date: prefill.date ?? (prefill.startsAt ? dateOf(prefill.startsAt, timeZone) : undefined),
    customer: prefill.customer?.id,
  }
}

/**
 * The three answers, and the two things that shape them.
 *
 * `slot` holds both ends as the API gave them rather than a start plus a length, for the reason
 * `withSlot` gives in `booking-window.ts`: the endpoint was asked where a length fits and
 * answered with an instant, so a loan across a daylight-saving boundary keeps the window it will
 * actually be stored with.
 */
export type FlowAnswers = {
  /** The tile. It decides the kind, whether step 2 is asked, and what is written at the end. */
  preset: DrivePreset
  kind: BookingKind
  /** Hours on `Extended`, days on `Two or three days`, and nothing on the other four tiles. */
  length: number
  vehicleId: string | null
  date: CalendarDate
  slot: FreeWindow | null
  /** Minutes for a test drive, whole days for a loan: what the last row of chips chooses. */
  duration: number
  customer: PickedCustomer | null
  /** The member the drive belongs to; `UNASSIGNED` is not offered here, the flow's is always someone. */
  assignedUserId: string
}

/** Where the flow starts: the dealership's defaults, plus whatever the caller already knew. */
export function initialAnswers({
  search,
  timeZone,
  defaults,
  userId,
  now = new Date(),
}: {
  search: BookingFlowSearch
  timeZone: string
  defaults: BookingsSettings
  userId: string
  now?: Date
}): FlowAnswers {
  // The tile leads: `?kind=loan` with no tile named is the loan tile, because the two used to be
  // the same control and a link written before the tiles existed still means what it said.
  const preset = search.preset ?? (search.kind === 'loan' ? 'loan' : DEFAULT_PRESET)
  const kind = PRESET_KINDS[preset]
  return {
    preset,
    kind,
    length: defaultLength(preset),
    vehicleId: search.vehicle ?? null,
    date: search.date ?? todayIn(timeZone, now),
    slot: null,
    duration: defaultDuration(kind, defaults),
    customer: null,
    assignedUserId: userId,
  }
}

/** Which tile a caller's prefill means. `Now` is the default and needs no entry in the URL. */
function presetFor(prefill: BookingPrefill): DrivePreset | undefined {
  if (prefill.date !== undefined || prefill.startsAt !== undefined) return 'later'
  return prefill.kind === 'loan' ? 'loan' : undefined
}

/** The chip that starts chosen: the dealership's own default, never the prototype's 60. */
export function defaultDuration(kind: BookingKind, defaults: BookingsSettings): number {
  return kind === 'loan' ? defaults.loan_default_days : defaults.test_drive_default_minutes
}

/** What the free-window read is asked for, whichever unit the chips are counting in. */
export function durationMinutes(answers: FlowAnswers): number {
  return answers.kind === 'loan' ? answers.duration * 24 * 60 : answers.duration
}

/**
 * A different car is free at different times, so the time goes. The same is true of a different
 * day and of a different length: each of the three is part of the question the grid was asked,
 * and an answer to a question that was not asked is the bug this function exists to prevent.
 */
export function withVehicle(answers: FlowAnswers, vehicleId: string): FlowAnswers {
  if (vehicleId === answers.vehicleId) return answers
  return { ...answers, vehicleId, slot: null }
}

export function withDay(answers: FlowAnswers, date: CalendarDate): FlowAnswers {
  if (date === answers.date) return answers
  return { ...answers, date, slot: null }
}

export function withDuration(answers: FlowAnswers, duration: number): FlowAnswers {
  if (duration === answers.duration) return answers
  return { ...answers, duration, slot: null }
}

/**
 * Switching tile re-seeds everything the tile decides: the kind, the length its chips count in,
 * and the step-2 duration that follows the kind. The chosen time goes with them — it was chosen
 * against a question this tile may not even ask.
 *
 * It is the only way the kind moves now: the kind used to be a toggle of its own on step 1, and
 * the tiles replaced it — `Loan vehicle` is one of the six.
 */
export function withPreset(
  answers: FlowAnswers,
  preset: DrivePreset,
  defaults: BookingsSettings,
): FlowAnswers {
  if (preset === answers.preset) return answers
  const kind = PRESET_KINDS[preset]
  return {
    ...answers,
    preset,
    kind,
    length: defaultLength(preset),
    duration: defaultDuration(kind, defaults),
    slot: null,
  }
}

/** One of the tile's own chips: three hours rather than two. Nothing else moves. */
export function withLength(answers: FlowAnswers, length: number): FlowAnswers {
  return length === answers.length ? answers : { ...answers, length }
}

/**
 * Whether the question that step asks has been answered.
 *
 * Step 2 is answered before it is asked on every tile but `Book for later`: those drives start at
 * the signature, so there is no time to choose and nothing to be missing.
 */
export function isAnswered(step: FlowStep, answers: FlowAnswers): boolean {
  if (step === 1) return answers.vehicleId !== null
  if (step === 2) return !asksWhen(answers.preset) || answers.slot !== null
  return answers.customer !== null
}

/**
 * Which step is actually on screen, given the one the URL asks for.
 *
 * A URL is typed, shared and reloaded, and none of those carry the answers — so `?step=3` on a
 * cold load is a request to book with no car and no time. Rather than draw a screen whose
 * CTA can never light up, the flow falls back to the first question still unanswered, which on a
 * cold load is question one.
 *
 * A step this tile does not ask is not a step: `?step=2` on a walk-in shows the driver question,
 * not a picker the flow has no use for.
 */
export function stepShown(requested: FlowStep | undefined, answers: FlowAnswers): FlowStep {
  const steps = flowSteps(answers.preset)
  const first = steps.find((step) => !isAnswered(step, answers)) ?? LAST_STEP
  const asked = requested ?? steps[0] ?? LAST_STEP
  return steps.includes(asked) && asked < first ? asked : first
}

/** The question the app bar asks, which is the screen's own name. */
export function stepQuestion(step: FlowStep): string {
  if (step === 1) return 'Which car?'
  if (step === 2) return 'When?'
  return 'Who is driving?'
}

/**
 * What the footer's button says. Only the last one writes anything, and what it writes depends
 * on the tile: `Book for later` plans, and every other tile hands the keys over.
 */
export function ctaLabel(step: FlowStep, preset: DrivePreset): string {
  if (step === 1) return 'Choose a car'
  if (step === 2) return 'Choose a time'
  return writesBooking(preset) ? 'Book it' : 'Review and sign'
}

/**
 * The three lengths a step-2 chip offers, with the dealership's own default among them.
 *
 * The prototype's three (30/60/90 minutes, 1/3/5 days) are kept, and a dealership whose default
 * is none of them gets a fourth chip rather than a preselected value it cannot get back to.
 */
export function durationChips(kind: BookingKind, defaults: BookingsSettings): number[] {
  const offered = kind === 'loan' ? [1, 3, 5] : [30, 60, 90]
  const preselected = defaultDuration(kind, defaults)
  return offered.includes(preselected) ? offered : [...offered, preselected].sort((a, b) => a - b)
}

/** `60` -> `60 min`, `3` -> `3 days`, in whichever unit this kind's chips count. */
export function durationLabel(kind: BookingKind, duration: number): string {
  if (kind !== 'loan') return `${duration} min`
  return duration === 1 ? '1 day' : `${duration} days`
}

export type DayChip = { date: CalendarDate; label: string }

/**
 * Today, tomorrow, and the next day the dealership is actually open — by name rather than as a
 * fixed date, which is what makes the third chip worth having on a Friday.
 *
 * Trading hours choose the third chip and restrict nothing: today and tomorrow are offered
 * whether or not the dealership trades on them, because opening hours "shape what the calendar
 * draws; they never restrict what can be booked". A week of closed days falls back to the day
 * after tomorrow rather than searching forever.
 */
export function dayChips(
  from: CalendarDate,
  hours: OpeningHours,
  chosen?: CalendarDate,
): DayChip[] {
  const tomorrow = addDays(from, 1)
  const nextOpen =
    [2, 3, 4, 5, 6, 7]
      .map((days) => addDays(from, days))
      .find((date) => !hours[dayKey(date)].closed) ?? addDays(from, 2)
  // The day the caller arrived on takes the third chip, so a booking started from a day further
  // out on the calendar has its own day to stand on rather than none of the three.
  const third = chosen && chosen !== from && chosen !== tomorrow ? chosen : nextOpen
  return [
    { date: from, label: 'Today' },
    { date: tomorrow, label: 'Tomorrow' },
    { date: third, label: dayChipLabel(third, from) },
  ]
}

/** How a day reads on a chip: its own word, or its date when it is further off than tomorrow. */
export function dayChipLabel(date: CalendarDate, from: CalendarDate): string {
  const word = dayWord(date, from)
  return word === 'today' ? 'Today' : word === 'tomorrow' ? 'Tomorrow' : word
}

/** The same day inside a sentence: `Free today`, `Free tomorrow`, `Free Mon 14`. */
export function dayWord(date: CalendarDate, from: CalendarDate): string {
  if (date === from) return 'today'
  if (date === addDays(from, 1)) return 'tomorrow'
  return `${weekdayShort(date)} ${dayOfMonth(date)}`
}

/**
 * The footer's line on the last step: `RAV4 · Today 11:30–12:30 · Priya Raman`.
 *
 * Null until there is something to say, which is what keeps the footer one row high on the two
 * steps before it.
 */
export function summaryLine({
  model,
  slot,
  from,
  timeZone,
  staff,
}: {
  model: string | undefined
  slot: FreeWindow | null
  /** Today, on the dealership's calendar: what `Today` and `Tomorrow` are relative to. */
  from: CalendarDate
  timeZone: string
  staff: string
}): string | null {
  if (!model || !slot) return null
  const when = whenWords({ startsAt: slot.starts_at, endsAt: slot.ends_at }, from, timeZone)
  return `${model} · ${when} · ${staff}`
}

/**
 * A window as the flow says it out loud: `Today 11:30–12:30`, `Tomorrow 09:00 – Mon 14 17:00`.
 *
 * The day is named once when there is one and twice when there are two, which is what makes a
 * three-day loan legible: a bare `09:00 – 17:00` on a card headed `Loan booked` leaves the reader
 * to guess which day it comes back on.
 *
 * The footer's summary and the confirmation's card both say it, which is why it is here rather
 * than inside either — the two are read seconds apart and must not disagree.
 *
 * `windowLabel` is the same sentence in the record's vocabulary, and the difference is the point:
 * a record is read weeks later and names the date (`Fri 11 Sep`), while everything this flow books
 * is near enough to be `Today` or `Tomorrow`, and it names the near day even when there is only
 * one of them. Two vocabularies, each right where it is read.
 */
export function whenWords(window: BookingWindow, from: CalendarDate, timeZone: string): string {
  const startDay = dateOf(window.startsAt, timeZone)
  const endDay = dateOf(window.endsAt, timeZone)
  const start = `${dayChipLabel(startDay, from)} ${timeOfDay(window.startsAt, timeZone)}`
  const end = timeOfDay(window.endsAt, timeZone)
  return startDay === endDay ? `${start}–${end}` : `${start} – ${dayChipLabel(endDay, from)} ${end}`
}

/**
 * One row of step 1's list: what the car is, and whether it can be picked at all today.
 *
 * Two reads make a row. `/vehicles/availability` is the authority on which cars exist for the
 * calendar and on what is holding each one; `GET /vehicles` is where the rego, the odometer and
 * the photograph live. The availability answer leads, so a car the calendar will not draw is not
 * offered here either, and a row whose record has not arrived still draws with what it has.
 */
/** Midnight to midnight on the dealership's clock, as `dayWindow` gives it. */
export type DayWindow = { from: string; to: string }

export type FlowVehicle = {
  id: string
  /** The name on the row: `Toyota RAV4 GXL`. */
  title: string
  /** What the toast and the labels call it: `RAV4`. */
  model: string
  /** `1AB2CD · 4210 · 48,210km`, or as much of it as the record gives. */
  identity: string
  vehicle: Vehicle | undefined
  state: VehicleState
}

/** The one signal in the row that says whether the car can be picked. */
export type VehicleState = {
  chip: string
  /** The token the chip is painted in: the prototype's `--ok`, `--warn` and `--faint`. */
  tone: 'ok' | 'warn' | 'faint'
  /** False draws the row at 55% and refuses the tap; the chip is the only other difference. */
  available: boolean
}

/**
 * What a car away for the whole day is away *for*, in CONTEXT.md's own Availability words:
 * `in use` is a car out on a test drive, `on loan` a car out on a loan.
 */
const AWAY_CHIPS: Record<BookingKind, string> = {
  loan: 'On loan',
  test_drive: 'In use',
}

/**
 * Which chip a car wears on the chosen day.
 *
 * The API answers one window at a time, and step 1 asks about the whole day, so `free` here means
 * "free all day" and the ways of not being free are told apart by the commitment that came back
 * with it:
 *
 * - a reason the car cannot be planned at all (an out-of-service car; a sold or disabled one is
 *   not in the list) is `Service`;
 * - a commitment covering the whole day is a car that is away rather than one with a gap in its
 *   day, and the chip names what it is away for — a test drive out on a walk-in used to read
 *   `On loan`, which is the wrong word in front of a salesperson, and is `In use`;
 * - anything else is a car with a busy patch, `Booked 11:00–12:00` for a plan and `Out
 *   11:00–12:00` for a car that has already gone, both still pickable: step 2 offers the windows
 *   either side of it.
 *
 * `free: false` with nothing named is no longer an answer the API gives; the fallback says only
 * as much as it knows rather than guessing at a loan.
 */
export function vehicleState(
  row: VehicleAvailability,
  day: DayWindow,
  timeZone: string,
): VehicleState {
  if (row.unbookable_reason) return { chip: 'Service', tone: 'faint', available: false }
  if (row.free) return { chip: 'Free', tone: 'ok', available: true }
  const held = row.held_by
  if (!held) return { chip: 'Not free', tone: 'faint', available: false }
  const allDay =
    Date.parse(held.starts_at) <= Date.parse(day.from) &&
    Date.parse(held.ends_at) >= Date.parse(day.to)
  if (allDay) return { chip: AWAY_CHIPS[held.kind], tone: 'faint', available: false }
  const verb = held.source === 'booking' ? 'Booked' : 'Out'
  return {
    chip: `${verb} ${timeOfDay(held.starts_at, timeZone)}–${timeOfDay(held.ends_at, timeZone)}`,
    tone: 'warn',
    available: true,
  }
}

/**
 * Step 1's list: every car the calendar can draw, nearest first.
 *
 * "Nearest" is read as soonest usable, because neither read knows where a car is parked: the ones
 * free all day, then the ones with a gap in the day, then the ones that are away, and stock
 * number inside each group so that the order does not shuffle as bookings land.
 */
export function vehicleRows({
  availability,
  vehicles,
  day,
  timeZone,
  query,
}: {
  availability: VehicleAvailability[]
  vehicles: Vehicle[]
  day: DayWindow
  timeZone: string
  query: string
}): FlowVehicle[] {
  const byId = new Map(vehicles.map((vehicle) => [vehicle.id, vehicle]))
  return availability
    .map((row) => toRow(row, byId.get(row.id), day, timeZone))
    .filter((row) => matches(row, query))
    .sort((a, b) => rank(a.state) - rank(b.state) || a.identity.localeCompare(b.identity))
}

function toRow(
  row: VehicleAvailability,
  vehicle: Vehicle | undefined,
  day: DayWindow,
  timeZone: string,
): FlowVehicle {
  const km = vehicle?.odometer_km
  return {
    id: row.id,
    title: [row.make, row.model, row.variant].filter(Boolean).join(' '),
    model: row.model,
    identity: [
      vehicle?.registration,
      row.stock_number,
      km === null || km === undefined ? null : `${km.toLocaleString('en-AU')}km`,
    ]
      .filter(Boolean)
      .join(' · '),
    vehicle,
    state: vehicleState(row, day, timeZone),
  }
}

function rank(state: VehicleState): number {
  if (!state.available) return 2
  return state.tone === 'ok' ? 0 : 1
}

/** `Rego, stock number, make`, as the placeholder promises, plus the model nobody types around. */
function matches(row: FlowVehicle, query: string): boolean {
  const typed = query.trim().toLowerCase()
  if (typed === '') return true
  return `${row.title} ${row.identity}`.toLowerCase().includes(typed)
}
